import { getMapperFromDiscountType } from '@domains/billing-settings/priv/CustomerDiscount/api/mappers'
import { FirstNFreeDiscountPlugin } from '@domains/billing-settings/priv/CustomerDiscount/factories/FirstNFreeDiscountFactory'
import { PerUnitAndFlatDiscountPlugin } from '@domains/billing-settings/priv/CustomerDiscount/factories/PerUnitAndFlatDiscountFactory'
import { PerUnitDiscountPlugin } from '@domains/billing-settings/priv/CustomerDiscount/factories/PerUnitDiscountFactory'
import { createCustomTheme } from '@domains/subscription/theme'
import { ThemeProvider } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useState } from 'react'
import {
  QueryKeys,
  useAllBillables,
  useCustomerBillableDiscounts,
  useCustomerBillablePrices,
  useCustomerTemplate,
  useDeleteCustomerDiscount,
  useTemplateBillablePrices,
} from '../api/queries'
import { CustomerDiscountData } from '../CustomerDiscountData'
import { CreateCustomerDiscountDialog } from '../components/CreateCustomerDiscountDialog'
import { CustomerDiscount } from '../components/CustomerDiscount'
import { FlatDiscountPlugin } from './FlatDiscountFactory'

interface CustomerDiscountFactoryProps {
  customerId: string
  currency: string
  onChange?: () => void
}

export const CustomerDiscountFactory = ({ customerId, currency, onChange }: CustomerDiscountFactoryProps) => {
  const { data: customerTemplate } = useCustomerTemplate(customerId)
  const { data: customerBillablePrices = [] } = useCustomerBillablePrices(customerId)
  const { data: templateBillablePrices = [] } = useTemplateBillablePrices(customerTemplate?.id)
  const { data: billables = [] } = useAllBillables()
  const { data: customerDiscounts = [] } = useCustomerBillableDiscounts(customerId)
  const { mutateAsync: deleteCustomerDiscount } = useDeleteCustomerDiscount(customerId)

  const queryClient = useQueryClient()

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)

  const customerDiscountManager = new CustomerDiscountData({
    billables,
    currency,
    customerBillablePrices,
    customerDiscounts,
    templateBillablePrices,
    enabledBillables: enabledProducts,
  })

  const filteredProducts = customerDiscountManager.filteredCustomerDiscountProducts()
  const items = customerDiscountManager.filteredDiscountItems()
  const activeFilteredProducts = filteredProducts.filter((product) => !product.disabled)

  const handleCreateDiscount = () => {
    setIsCreateDialogOpen(false)
    queryClient.invalidateQueries({ queryKey: QueryKeys.CustomerBillableDiscounts(customerId) })
    onChange?.()
  }

  const handleDeleteDiscount = async (id: string) => {
    await deleteCustomerDiscount(id, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.CustomerBillableDiscounts(customerId) })
        onChange?.()
      },
    })
  }

  const getDiscountLabel = (discountType: string) => {
    const mapper = getMapperFromDiscountType(discountType)

    return mapper?.discountTypeLabel ?? discountType
  }

  return (
    <ThemeProvider theme={createCustomTheme()}>
      <CreateCustomerDiscountDialog
        currency={currency}
        customerId={customerId}
        plugins={[
          new FlatDiscountPlugin(),
          new PerUnitDiscountPlugin(),
          new PerUnitAndFlatDiscountPlugin(),
          new FirstNFreeDiscountPlugin(),
        ]}
        open={isCreateDialogOpen}
        onCreateDiscount={handleCreateDiscount}
        onClose={() => setIsCreateDialogOpen(false)}
        products={filteredProducts}
      />
      <CustomerDiscount
        items={{ data: items, isLoading: false }}
        onDeleteDiscount={handleDeleteDiscount}
        onCreateDiscount={() => setIsCreateDialogOpen(true)}
        canCreateDiscount={activeFilteredProducts.length > 0}
        format={{
          price: ({ amount }) => tryFormatAmount(amount),
          discount: ({ value, adornment }) => tryFormatDiscount({ value, adornment }),
          expirationDate: (date) => {
            if (!date) return 'No expiration'

            return dayjs(date).format('YYYY-MM-DD')
          },
        }}
        getDiscountLabel={getDiscountLabel}
      />
    </ThemeProvider>
  )
}

const tryFormatAmount = ({ value, currency }: { value: number; currency: string }) => {
  try {
    return formatAmount({ value, currency })
  } catch {
    return undefined
  }
}

const formatAmount = ({ value, currency }: { value: number; currency: string }) => {
  return new Intl.NumberFormat('sv-SE', { style: 'currency', currency }).format(value)
}

const tryFormatDiscount = ({ value, adornment }: { value: number; adornment: string }) => {
  try {
    return formatDiscount({ value, adornment })
  } catch {
    return undefined
  }
}

const formatDiscount = ({ value, adornment }: { value: number; adornment: string }) => {
  if (adornment === '%') {
    return `${value}${adornment}`
  }

  return formatAmount({ value, currency: adornment })
}

const enabledProducts = [
  'b50f5512-d09a-4bef-bf55-f5f6871e47c3',
  'ab32e409-e943-4f56-bde1-c634901ebad0',
  '7bc92d04-7beb-492d-bba2-60919560ecc3',
  'f495ce7e-fb0d-427e-8ff0-b182d1caa4cc',
]
